import { useEffect, useRef, useState } from 'react'
import type { FieldProps } from '@rjsf/utils'
import { ClockCounterClockwiseIcon, Mtds, TilsynsobjektV2 } from 'prove-common'
import { useGetLegalEntitiesBySiteNrQuery } from '../../../hooks/useFiskeriDir'
import { useGetTilsynsobjekterQuery } from '../../../hooks/useTilsynsobjekt'
import {
  getLocalStorageItem,
  setLocalStorageItem
} from '../../../utils/localStorageUtils'
import { requiredFieldMessage } from '../../../utils/requiredFieldMessage'
import { findTilsynsobjektProdusentnummer } from '../../../utils/tilsynsobjektUtils'
import {
  ExternalReference,
  ExternalReferenceType,
  LegalEntity
} from '../../../types/types'

interface ExternalReferenceFieldUiOptions {
  externalReferenceType?: ExternalReferenceType
}

interface LookupResult {
  id: string
  reference: ExternalReference
}

interface LookupOption {
  label: string
  value: string
}

interface LookupDetailRow {
  label: string
  value: string | undefined
}

interface GenericLookupProps {
  fieldProps: Readonly<FieldProps>
  onSearchTextChange: (value: string) => void
  lookupResults: LookupResult[]
  isFetching: boolean
  placeholder: string
  inputMode?: 'search' | 'numeric' | 'text'
  inputDataCy: string
  listDataCy: string
  recentStorageKey: string
  selectionFn: (reference: ExternalReference | undefined) => LookupOption[]
  renderOption: (
    result: LookupResult,
    recentlySelectedResults: LookupResult[]
  ) => React.ReactNode
  detailSummary?: string
  detailRows?: (reference: ExternalReference | undefined) => LookupDetailRow[]
  showDetails?: (reference: ExternalReference | undefined) => boolean
  maxRecentItems?: number
}

const SEARCH_DEBOUNCE_MS = 500
const DEFAULT_MAX_RECENT_ITEMS = 3
const PRODUSENTER_RECENT_STORAGE_KEY = 'recently-selected-produsenter'
const LOKALITETER_RECENT_STORAGE_KEY = 'recently-selected-lokaliteter'

const isValidSiteNr = (value: string): boolean => /^\d+$/.test(value)
const isNonEmptyString = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.length > 0

const isStoredLookupResult = (
  value: Partial<LookupResult>
): value is LookupResult => {
  const reference = value.reference as Partial<ExternalReference> | undefined
  return (
    isNonEmptyString(value.id) &&
    reference != null &&
    (reference.externalReferenceId == null ||
      isNonEmptyString(reference.externalReferenceId))
  )
}

const collectErrorMessages = (errorSchema: unknown): string[] => {
  if (errorSchema == null || typeof errorSchema !== 'object') return []

  const schema = errorSchema as Record<string, unknown>
  const ownErrors = Array.isArray(schema.__errors)
    ? schema.__errors.filter(
        (error): error is string => typeof error === 'string'
      )
    : []
  const nestedErrors = Object.entries(schema).flatMap(([key, value]) =>
    key === '__errors' ? [] : collectErrorMessages(value)
  )

  return [...ownErrors, ...nestedErrors]
}

const getFieldErrorMessages = (
  rawErrors: string[] | undefined,
  errorSchema: unknown,
  title: string | undefined
) => {
  if (rawErrors && rawErrors.length > 0) return rawErrors

  const nestedErrors = collectErrorMessages(errorSchema)
  if (nestedErrors.length === 0) return []
  if (!title) return nestedErrors

  return [requiredFieldMessage(title)]
}

const withMostRecentFirst = (
  recentlySelectedResults: LookupResult[],
  result: LookupResult,
  maxRecentItems: number
): LookupResult[] => {
  if (maxRecentItems <= 0) {
    return []
  }

  return [
    result,
    ...recentlySelectedResults.filter((value) => value.id !== result.id)
  ].slice(0, maxRecentItems)
}

const isRecentlySelected = (
  result: LookupResult,
  recentlySelectedResults: LookupResult[]
): boolean =>
  recentlySelectedResults.some((recentResult) => recentResult.id === result.id)

const createRenderLookupOption = (
  getPrimary: (result: LookupResult) => string | undefined,
  getSecondary?: (result: LookupResult) => string | undefined
) => {
  function renderLookupOption(
    result: LookupResult,
    recentlySelectedResults: LookupResult[]
  ): React.ReactNode {
    return (
      <Mtds.Flex data-justify='space-between' data-nowrap>
        <Mtds.Flex data-nowrap data-align='center'>
          {isRecentlySelected(result, recentlySelectedResults) && (
            <ClockCounterClockwiseIcon aria-label='Nylig valgt' />
          )}
          {getPrimary(result)}
        </Mtds.Flex>
        <Mtds.Muted className='text-end'>{getSecondary?.(result)}</Mtds.Muted>
      </Mtds.Flex>
    )
  }

  return renderLookupOption
}

const mapTilsynsobjekterToLookupResults = (
  tilsynsobjekter: TilsynsobjektV2[]
): LookupResult[] => {
  const mapped: LookupResult[] = []

  for (const tilsynsobjekt of tilsynsobjekter) {
    const idstring = tilsynsobjekt.idstring
    const virksomhetsnavn = tilsynsobjekt.virksomhetsnavn
    if (isNonEmptyString(idstring) && isNonEmptyString(virksomhetsnavn)) {
      mapped.push({
        id: idstring,
        reference: {
          externalReferenceId: findTilsynsobjektProdusentnummer(tilsynsobjekt),
          organisasjonsnummer: tilsynsobjekt.organisasjonsnummer,
          virksomhetsnavn: virksomhetsnavn,
          tilsynsobjektId: idstring,
          aktivitet: tilsynsobjekt.aktivitet?.beskrivelse ?? '-'
        }
      })
    }
  }

  return mapped
}

const mapLegalEntitiesToLookupResults = (
  legalEntities: LegalEntity[],
  siteNr: number
): LookupResult[] => {
  const mapped: LookupResult[] = []

  for (const entity of legalEntities) {
    const openLegalEntityNr = entity.openLegalEntityNr
    const legalEntityName = entity.legalEntityName
    if (
      isNonEmptyString(openLegalEntityNr) &&
      isNonEmptyString(legalEntityName)
    ) {
      mapped.push({
        id: openLegalEntityNr + ':' + siteNr,
        reference: {
          externalReferenceId: siteNr.toString(),
          organisasjonsnummer: entity.openLegalEntityNr,
          virksomhetsnavn: entity.legalEntityName,
          tilsynsobjektId: undefined,
          aktivitet: undefined
        }
      })
    }
  }

  return mapped
}

const readRecentlySelectedResults = (storageKey: string): LookupResult[] => {
  try {
    const stored = getLocalStorageItem<Array<Partial<LookupResult>>>(storageKey)
    if (stored == null) {
      return []
    }

    return stored.filter(isStoredLookupResult)
  } catch {
    return []
  }
}

const persistRecentlySelectedResults = (
  storageKey: string,
  results: LookupResult[]
): void => {
  setLocalStorageItem(storageKey, JSON.stringify(results))
}

function LookupField(props: Readonly<GenericLookupProps>) {
  const {
    fieldProps,
    onSearchTextChange,
    lookupResults,
    isFetching,
    placeholder,
    inputMode,
    inputDataCy,
    listDataCy,
    recentStorageKey,
    selectionFn,
    renderOption,
    detailSummary,
    detailRows,
    showDetails,
    maxRecentItems = DEFAULT_MAX_RECENT_ITEMS
  } = props

  const {
    id,
    formData,
    onChange,
    disabled,
    readonly,
    required,
    rawErrors,
    errorSchema,
    schema,
    fieldPathId
  } = fieldProps
  const { $id, path } = fieldPathId
  const errorMessages = Array.from(
    new Set(getFieldErrorMessages(rawErrors, errorSchema, schema.title))
  )

  const timer = useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(
    undefined
  )

  useEffect(
    () => () => {
      if (timer.current != null) {
        globalThis.clearTimeout(timer.current)
      }
    },
    []
  )

  const [recentlySelectedResults, setRecentlySelectedResults] = useState<
    LookupResult[]
  >(() => readRecentlySelectedResults(recentStorageKey))

  useEffect(() => {
    setRecentlySelectedResults(readRecentlySelectedResults(recentStorageKey))
  }, [recentStorageKey])

  const visibleResults =
    lookupResults.length === 0 ? recentlySelectedResults : lookupResults
  const typedExternalReference = formData as ExternalReference | undefined
  const selected = selectionFn(typedExternalReference)
  const allResults = [...lookupResults, ...recentlySelectedResults]

  const findResultById = (
    selectedValue: string | undefined
  ): LookupResult | undefined =>
    allResults.find((result) => result.id === selectedValue)

  const onClick = (result: LookupResult): void => {
    onChange(result.reference, path)
    const updatedRecentlySelected = withMostRecentFirst(
      recentlySelectedResults,
      result,
      maxRecentItems
    )
    setRecentlySelectedResults(updatedRecentlySelected)
    persistRecentlySelectedResults(recentStorageKey, updatedRecentlySelected)
  }

  const handleSearchInput = (event: React.InputEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    if (timer.current != null) {
      globalThis.clearTimeout(timer.current)
    }
    timer.current = globalThis.setTimeout(
      () => onSearchTextChange(value),
      SEARCH_DEBOUNCE_MS
    )
  }

  return (
    <div id={$id}>
      <Mtds.Field.Suggestion
        selected={selected}
        onSelectedChange={(items) => {
          const result = findResultById(items[0]?.value)
          if (result) {
            onClick(result)
          } else if (items.length === 0 && selected.length > 0) {
            onChange(undefined, path)
          }
        }}
      >
        <Mtds.Input
          id={id}
          aria-labelledby={`${$id}_label`}
          type='search'
          inputMode={inputMode}
          autoComplete='off'
          placeholder={placeholder}
          onInput={handleSearchInput}
          disabled={disabled}
          required={required}
          readOnly={readonly}
          data-cy={inputDataCy}
        />
        <button type='reset' aria-label='Fjern tekst' />
        <Mtds.Field.Datalist data-nofilter data-cy={listDataCy}>
          {isFetching && (
            <Mtds.Field.Option key='loading' value='loading'>
              Laster...
            </Mtds.Field.Option>
          )}
          {!isFetching &&
            visibleResults.map((result) => (
              <Mtds.Field.Option key={result.id} value={result.id}>
                {renderOption(result, recentlySelectedResults)}
              </Mtds.Field.Option>
            ))}
        </Mtds.Field.Datalist>
      </Mtds.Field.Suggestion>
      {errorMessages.map((errorMessage) => (
        <Mtds.Validation key={`${id}-${errorMessage}`}>
          {errorMessage}
        </Mtds.Validation>
      ))}

      {detailSummary && detailRows && showDetails?.(typedExternalReference) && (
        <Mtds.Details data-variant='card' open className='mt-2'>
          <Mtds.Details.Summary>{detailSummary}</Mtds.Details.Summary>
          <Mtds.Table data-border='none' data-fixed>
            <tbody>
              {detailRows(typedExternalReference).map((row) => (
                <tr key={row.label}>
                  <th>{row.label}</th>
                  <td>{row.value ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </Mtds.Table>
        </Mtds.Details>
      )}
    </div>
  )
}

const produsentSelection = (
  reference: ExternalReference | undefined
): LookupOption[] => {
  if (reference?.tilsynsobjektId == null) {
    return []
  }
  return [
    {
      label: reference.virksomhetsnavn ?? reference.tilsynsobjektId,
      value: reference.tilsynsobjektId
    }
  ]
}

const produsentDetailRows = (
  reference: ExternalReference | undefined
): LookupDetailRow[] => {
  return [
    { label: 'Virksomhetsnavn', value: reference?.virksomhetsnavn },
    { label: 'Aktivitet', value: reference?.aktivitet },
    { label: 'Produsentnummer', value: reference?.externalReferenceId },
    {
      label: 'Organisasjonsnummer',
      value: reference?.organisasjonsnummer
    }
  ]
}

const lokalitetSelection = (
  reference: ExternalReference | undefined
): LookupOption[] => {
  if (reference?.externalReferenceId == null) {
    return []
  }
  return [
    {
      label: reference.externalReferenceId,
      value: reference.externalReferenceId
    }
  ]
}

const lokalitetDetailRows = (
  reference: ExternalReference | undefined
): LookupDetailRow[] => {
  return [
    {
      label: 'Lokalitetsnummer',
      value: reference?.externalReferenceId
    },
    { label: 'Virksomhetsnavn', value: reference?.virksomhetsnavn },
    {
      label: 'Organisasjonsnummer',
      value: reference?.organisasjonsnummer
    }
  ]
}

function ProdusentLookupField(props: Readonly<FieldProps>) {
  const [searchText, setSearchText] = useState<string>('')
  const { data: tilsynsobjekter, isFetching } =
    useGetTilsynsobjekterQuery(searchText)
  const lookupResults = tilsynsobjekter
    ? mapTilsynsobjekterToLookupResults(tilsynsobjekter)
    : []

  return (
    <LookupField
      fieldProps={props}
      onSearchTextChange={setSearchText}
      lookupResults={lookupResults}
      isFetching={isFetching}
      placeholder='Søk etter produsentnummer eller -navn'
      inputDataCy='produsent-input'
      listDataCy='produsent-list'
      recentStorageKey={PRODUSENTER_RECENT_STORAGE_KEY}
      selectionFn={produsentSelection}
      renderOption={createRenderLookupOption(
        (result) => result.reference.virksomhetsnavn ?? result.id,
        (result) => result.reference.aktivitet
      )}
      detailSummary='Detaljer for valgt produsent'
      showDetails={(reference) => reference?.tilsynsobjektId != null}
      detailRows={produsentDetailRows}
    />
  )
}

function LokalitetLookupField(props: Readonly<FieldProps>) {
  const [searchText, setSearchText] = useState<string>('')
  const siteNr = isValidSiteNr(searchText) ? Number(searchText) : undefined
  const { data: legalEntities, isFetching } =
    useGetLegalEntitiesBySiteNrQuery(siteNr)
  const lookupResults =
    legalEntities && siteNr
      ? mapLegalEntitiesToLookupResults(legalEntities, siteNr)
      : []

  return (
    <LookupField
      fieldProps={props}
      onSearchTextChange={setSearchText}
      lookupResults={lookupResults}
      isFetching={isFetching}
      placeholder='Skriv inn lokalitetsnummer'
      inputMode='numeric'
      inputDataCy='lokalitet-input'
      listDataCy='lokalitet-list'
      recentStorageKey={LOKALITETER_RECENT_STORAGE_KEY}
      maxRecentItems={DEFAULT_MAX_RECENT_ITEMS}
      selectionFn={lokalitetSelection}
      renderOption={createRenderLookupOption(
        (result) => result.reference.externalReferenceId ?? '-',
        (result) =>
          `${result.reference.virksomhetsnavn ?? '-'} (${result.reference.organisasjonsnummer ?? '-'})`
      )}
      detailSummary='Detaljer for valgt lokalitet'
      showDetails={(reference) => reference?.organisasjonsnummer != null}
      detailRows={lokalitetDetailRows}
    />
  )
}

const getTypeFromProps = (
  props: Readonly<FieldProps>
): ExternalReferenceType => {
  const externalReferenceType = (
    props.uiSchema?.['ui:options'] as
      ExternalReferenceFieldUiOptions | undefined
  )?.externalReferenceType

  return externalReferenceType === 'LOKALITET' ? 'LOKALITET' : 'PRODUSENT'
}

export function ExternalReferenceField(props: Readonly<FieldProps>) {
  const externalReferenceType = getTypeFromProps(props)
  const LookupComponent =
    externalReferenceType === 'LOKALITET'
      ? LokalitetLookupField
      : ProdusentLookupField
  return <LookupComponent {...props} />
}
